import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EntityLinkSelector } from '@/components/shared/EntityLinkSelector';
import type { EntityType } from '@/types/link';

interface SelectedEntity {
  id: string;
  type: EntityType;
  name: string;
}

interface SupplierCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: any, links?: SelectedEntity[]) => void;
  isCreating?: boolean;
}

export function SupplierCreateDialog({ open, onOpenChange, onSave, isCreating }: SupplierCreateDialogProps) {
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    criticality: 'medium',
    notes: '',
  });
  const [selectedLinks, setSelectedLinks] = useState<SelectedEntity[]>([]);

  const handleSubmit = () => {
    onSave(formData, selectedLinks);
    setFormData({
      name: '',
      category: '',
      criticality: 'medium',
      notes: '',
    });
    setSelectedLinks([]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add New Supplier</DialogTitle>
          <DialogDescription>
            Add a third-party supplier or vendor to your organization.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="name">Supplier Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Cloud Storage Provider Inc."
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="category">Category</Label>
            <Input
              id="category"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              placeholder="e.g., Cloud Services, IT Hardware, Software"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="criticality">Criticality</Label>
            <Select
              value={formData.criticality}
              onValueChange={(value) => setFormData({ ...formData, criticality: value })}
            >
              <SelectTrigger id="criticality">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional information about this supplier..."
              rows={4}
            />
          </div>

          <EntityLinkSelector
            allowedTypes={['asset', 'system', 'site', 'zone', 'risk', 'control', 'evidence', 'incident']}
            onLinksChange={setSelectedLinks}
            initialLinks={selectedLinks}
          />
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={!formData.name || isCreating}
          >
            {isCreating ? 'Creating...' : 'Create Supplier'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
